import { isEqual } from 'lodash';
import { computed, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import { useFlashesStore, useIntegrationProfileStore } from '@/stores';
import {
  API_FILTER_INTEGRATION_TYPE,
  type IntegrationProfile,
  type MinimalIntegrationProfile,
} from '@/types/integration_profiles';
import {
  fillStandardPopulationSettings,
  removeConfigType,
} from '@/utils/integrationProfileSettings';

export default function useApiFilterSettings(profileId = '') {
  const { hasPermission } = useAuth();
  const integrationProfileStore = useIntegrationProfileStore();
  const flashesStore = useFlashesStore();

  const name = ref('');
  const localSettings = ref<MinimalIntegrationProfile | null>(null);
  const isSaving = ref(false);

  const filter = computed(() =>
    integrationProfileStore.getIntegrationProfileById(profileId),
  );

  const savedSettings = computed<MinimalIntegrationProfile>(() => ({
    population_settings: filter.value?.population_settings ?? [],
    standard_population_settings: fillStandardPopulationSettings(
      filter.value?.standard_population_settings,
    ),
    exclude_partnerships_created_since:
      filter.value?.exclude_partnerships_created_since ?? null,
  }));

  const currentSettings = computed(
    () => localSettings.value ?? savedSettings.value,
  );

  const canManage = computed(() => hasPermission('manage:integrations'));

  const hasUnsavedChanges = computed(
    () =>
      name.value.trim() !== (filter.value?.name ?? '') ||
      !isEqual(
        removeConfigType(currentSettings.value),
        removeConfigType(savedSettings.value),
      ),
  );

  const canSave = computed(
    () =>
      !!name.value.trim() &&
      hasUnsavedChanges.value &&
      !isSaving.value &&
      canManage.value,
  );

  function initialize() {
    name.value = filter.value?.name ?? '';
    localSettings.value = null;
  }

  function onSettingsUpdated(payload: MinimalIntegrationProfile) {
    localSettings.value = payload;
  }

  function onUpdateExcludePartnershipsCreatedSince(isChecked: boolean) {
    localSettings.value = {
      ...currentSettings.value,
      exclude_partnerships_created_since: isChecked
        ? null
        : savedSettings.value.exclude_partnerships_created_since ||
          new Date().toISOString(),
    };
  }

  /** Saves the filter and resolves with its id, or undefined when the request failed. */
  async function save() {
    if (!canSave.value) return undefined;
    isSaving.value = true;
    try {
      const settings = removeConfigType(currentSettings.value);
      const trimmedName = name.value.trim();
      const savedFilter = filter.value
        ? await updateFilter(filter.value, settings, trimmedName)
        : await createFilter(settings, trimmedName);
      if (!savedFilter) return undefined;
      integrationProfileStore.saveIntegrationProfileInStore(savedFilter);
      await integrationProfileStore.refreshIntegrationProfileStore();
      localSettings.value = null;
      flashesStore.addSuccessFlash({ message: 'Filter saved' });
      return savedFilter.profile_id;
    } finally {
      isSaving.value = false;
    }
  }

  async function createFilter(
    settings: MinimalIntegrationProfile,
    trimmedName: string,
  ): Promise<IntegrationProfile | undefined> {
    const { data, error } =
      await integrationProfileStore.postIntegrationProfileSettings({
        ...settings,
        integration_type: API_FILTER_INTEGRATION_TYPE,
        name: trimmedName,
      });
    if (error || !data) {
      flashesStore.addErrorFlash({ message: 'Failed to create filter' });
      return undefined;
    }
    return {
      ...data,
      calculation_status: { status: 'pending', last_completed_at: null },
    };
  }

  async function updateFilter(
    savedFilter: IntegrationProfile,
    settings: MinimalIntegrationProfile,
    trimmedName: string,
  ) {
    try {
      await integrationProfileStore.putIntegrationProfileSettings(
        savedFilter.profile_id,
        { ...settings, name: trimmedName },
      );
      return { ...savedFilter, ...settings, name: trimmedName };
    } catch {
      flashesStore.addErrorFlash({ message: 'Failed to save filter' });
      return undefined;
    }
  }

  return {
    filter,
    name,
    currentSettings,
    canManage,
    hasUnsavedChanges,
    canSave,
    isSaving,
    initialize,
    onSettingsUpdated,
    onUpdateExcludePartnershipsCreatedSince,
    save,
  };
}
